import { useMemo } from 'react';
import GlCodeApprovalCell from 'src/components/custom-cells/GlCodeApprovalCell';
import { GridColumn } from 'src/components/GridWrapper';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import LineItemInfo from 'src/sections/invoice_details/lineItemsColumns/LineItemInfo';
import BooleanFilterCell from 'src/sections/view_checkbook/view_period_cell/BooleanFilterCell';
import {
  ActiveFilterCell,
  FixedFilterCell,
} from 'src/sections/view_checkbook/view_period_cell/BooleanFilterMenu';
import BudgetAmountDisplayCell from 'src/sections/view_checkbook/view_period_cell/BudgetAmountDisplayCell';
import BudgetAmountDollarCell from 'src/sections/view_checkbook/view_period_cell/BudgetAmountDollarCell';
import BudgetAmountDollarDisplayCell from 'src/sections/view_checkbook/view_period_cell/BudgetAmountDollarDisplayCell';
import BudgetAmountPercentage from 'src/sections/view_checkbook/view_period_cell/BudgetAmountPercentage';
import BudgetAmountPercentageCell from 'src/sections/view_checkbook/view_period_cell/BudgetAmountPercentageCell';
import CommittedBalance from 'src/sections/view_checkbook/view_period_cell/CommittedBalance';
import DepartmentCell from 'src/sections/view_checkbook/view_period_cell/DepartmentCell';
import DescriptionCell from 'src/sections/view_checkbook/view_period_cell/DescriptionCell';
import ForecastAmountCell from 'src/sections/view_checkbook/view_period_cell/ForecastAmountCell';
import GLCode from 'src/sections/view_checkbook/view_period_cell/GLCode';
import { GLCodeApprovalFilterCell } from 'src/sections/view_checkbook/view_period_cell/GlCodeApprovalFilterMenu';
import JournalEntry from 'src/sections/view_checkbook/view_period_cell/JournalEntry';
import TransactionLog from 'src/sections/view_checkbook/view_period_cell/TransactionLog';
import SpentOrHoldBalance from '../../../sections/view_checkbook/view_period_cell/SpentOrHoldBalance';

export const validateNumber = (field: string, data: any) => {
  const value = Number(data[field]);
  const isFinite = Number.isFinite(value);
  if (isFinite && value < 0) {
    return {
      backgroundColor: '#EFB2B2',
    };
  } else if (isFinite && value > 0) {
    return {
      backgroundColor: '#C3D7B9',
    };
  }
  return {};
};

const useCheckbookPeriodColumns = (isEditMode: boolean, isColumnMenuDisabled: boolean) => {
  const {
    t,
    hasAdjustBudgetPermission,
    selectedPeriodDepartment,
    hasUsesInitialBudgetsPermission,
    isCheckbookEditable,
  } = useViewCheckbook();

  const viewCheckbookColumns: GridColumn<any>[] = useMemo(
    () => [
      {
        field: 'selected',
        title: '',
        filter: 'boolean',
        menu: false,
        sortable: false,
      },
      ...(isEditMode
        ? ([
            {
              field: 'errors',
              title: '',
              width: 40,
              component: LineItemInfo,
              menu: false,
              sortable: false,
            },
          ] as const)
        : []),

      {
        field: '',
        title: 'General Ledger',
        headerClassName: 'budget-amt-header',
        locked: false,
        sortable: false,
        width: 130,
        menu: false,
        show: true,
        childrenColumn: [
          ...(selectedPeriodDepartment.departmentId === 0
            ? ([
                {
                  field: 'departments',
                  title: 'Dept.',
                  component: DepartmentCell,
                  width: 80,
                  menu: false,
                  headerCellTooltip: 'List of departments that use this GL code',
                  sortable: false,
                  show: true,
                },
              ] as GridColumn<any>[])
            : []),
          {
            field: 'code',
            title: t('Code'),
            filter: 'text',
            component: GLCode,
            width: 120,
            sortable: true,
            menu: !isColumnMenuDisabled,
          },
          {
            field: 'description',
            title: t('Description'),
            filter: 'text',
            component: DescriptionCell,
            width: 250,
            sortable: true,
            menu: !isColumnMenuDisabled,
            show: true,
          },
        ],
      },

      {
        field: '',
        title: 'Checkbook Amounts',
        headerClassName: 'budget-amt-header',
        locked: false,
        sortable: false,
        width: 130,
        menu: false,
        show: true,
        childrenColumn: [
          ...(hasUsesInitialBudgetsPermission
            ? ([
                {
                  field: 'initialBudget',
                  title: 'Budget',
                  filter: 'numeric',
                  format: '{0:c2}',
                  filterFormat: 'c',
                  dataType: 'dollar',
                  component: BudgetAmountDisplayCell,
                  sortable: true,
                  menu: !isColumnMenuDisabled,
                  width: 120,
                  show: true,
                },
              ] as const)
            : []),
          {
            field: 'budgetAmount',
            title: 'Forecast',
            filter: 'numeric',
            format: '{0:c2}',
            filterFormat: 'c',
            dataType: 'dollar',
            component: ForecastAmountCell,
            sortable: true,
            menu: !isColumnMenuDisabled,
            width: 120,
            show: true,
          },
          {
            field: 'holdAmount',
            title: t('Planned'),
            filter: 'numeric',
            format: '{0:c2}',
            filterFormat: 'c',
            component: SpentOrHoldBalance,
            dataType: 'dollar',
            sortable: true,
            menu: !isColumnMenuDisabled,
            width: 120,
            show: true,
          },
          {
            field: 'committedAmount',
            title: t('Committed'),
            filter: 'numeric',
            format: '{0:c2}',
            filterFormat: 'c',
            component: CommittedBalance,
            dataType: 'dollar',
            sortable: true,
            menu: !isColumnMenuDisabled,
            width: 120,
            show: true,
          },
          {
            field: 'spentAmount',
            title: t('Actualized'),
            filter: 'numeric',
            format: '{0:c2}',
            filterFormat: 'c',
            dataType: 'dollar',
            component: SpentOrHoldBalance,
            sortable: true,
            menu: !isColumnMenuDisabled,
            width: 120,
            show: true,
          },
        ],
      },
      ...(hasUsesInitialBudgetsPermission
        ? ([
            {
              field: 'budget',
              title: 'Remaining vs. Budget',
              headerClassName: 'budget-amt-header',
              locked: false,
              sortable: false,
              width: 130,
              menu: false,
              show: true,
              childrenColumn: [
                {
                  field: 'remainingInitialBudgetAmount',
                  title: '$',
                  filter: 'numeric',
                  format: '{0:c2}',
                  filterFormat: 'c',
                  dataType: 'dollar' as const,
                  component: BudgetAmountDollarDisplayCell,
                  sortable: true,
                  menu: !isColumnMenuDisabled,
                  width: 100,
                  show: true,
                },
                {
                  field: 'remainingInitialBudgetAmountPercentage',
                  title: '%',
                  filter: 'numeric',
                  format: '{0:P2}',
                  filterFormat: 'p2',
                  component: BudgetAmountPercentageCell,
                  sortable: true,
                  menu: !isColumnMenuDisabled,
                  width: 100,
                  show: true,
                },
              ] as GridColumn<any>[],
            },
          ] as GridColumn<any>[])
        : []),
      {
        field: 'forecast',
        title: 'Remaining vs. Forecast',
        headerClassName: 'budget-amt-header',
        locked: false,
        sortable: false,
        width: 130,
        menu: false,
        show: true,
        childrenColumn: [
          {
            field: 'remainingBudgetAmount',
            title: `$`,
            filter: 'numeric',
            format: '{0:c2}',
            filterFormat: 'c',
            dataType: 'dollar' as const,
            component: BudgetAmountDollarCell,
            sortable: true,
            menu: !isColumnMenuDisabled,
            width: 100,
            show: true,
          },
          {
            field: 'remainingBudgetAmountPercentage',
            title: `% `,
            filter: 'numeric',
            format: '{0:P2}',
            filterFormat: 'p2',

            component: BudgetAmountPercentage,
            sortable: true,
            menu: !isColumnMenuDisabled,
            width: 100,
            show: true,
          },
        ],
      },

      ...(isEditMode && isCheckbookEditable && hasAdjustBudgetPermission
        ? ([
            {
              field: 'journalEntry',
              title: 'Journal Entry',
              width: 100,
              component: JournalEntry,
              menu: false,
              sortable: false,
              show: true,
            },
          ] as const)
        : []),
      {
        field: 'transaction',
        title: 'Transaction Log',
        width: 120,
        component: TransactionLog,
        menu: false,
        sortable: false,
        show: true,        
        headerClassName:'transactionView'
      },
      {
        field: '',
        title: 'GL Code Type',
        headerClassName: 'budget-amt-header',
        locked: false,
        sortable: false,
        width: 130,
        menu: false,
        show: true,
        childrenColumn: [
          {
            field: 'isFixed',
            title: 'Fixed Code',
            component: BooleanFilterCell,
            columnMenu: FixedFilterCell,
            sortable: true,
            menu: !isColumnMenuDisabled,
            width: 135,
            show: true,
          },
          {
            field: 'isActive',
            title: t('Active Code'),
            component: BooleanFilterCell,
            columnMenu: ActiveFilterCell,
            sortable: true,
            menu: !isColumnMenuDisabled,
            width: 135,
            show: true,
          },
        ],
      },
      {
        field: '',
        title: 'Approval Exceptions',
        headerClassName: 'budget-amt-header',
        locked: false,
        sortable: false,
        width: 130,
        menu: false,
        show: true,
        childrenColumn: [
          {
            field: 'poApproval',
            title: t('PO '),
            component: GlCodeApprovalCell,
            sortable: true,
            menu: !isColumnMenuDisabled,
            width: 100,
            show: true,
            columnMenu: GLCodeApprovalFilterCell,
            minResizableWidth: 90,
          },
          {
            field: 'invoiceApproval',
            title: t('Invoice '),
            component: GlCodeApprovalCell,
            sortable: true,
            menu: !isColumnMenuDisabled,
            width: 100,
            show: true,
            columnMenu: GLCodeApprovalFilterCell,
            minResizableWidth: 90,
          },
        ],
      },
    ],
    [
      hasAdjustBudgetPermission,
      isColumnMenuDisabled,
      isCheckbookEditable,
      isEditMode,
      selectedPeriodDepartment.departmentId,
      t,
      hasUsesInitialBudgetsPermission,
    ]
  );

  return viewCheckbookColumns;
};

export default useCheckbookPeriodColumns;
